import axios from 'axios';
import {
	API_ENDPOINTS,
	DataTable,
	formatDate,
	FormInput,
	Icon,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	route,
	StringHelpers,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
	customToast,
	replaceKeyWithValue,
} from 'carrier-fe';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface MediaPack {
	id: string;
	theme_id: string;
	title: string;
	thumbnail_url: string | null;
	download_url: string | null;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
	permissions: {
		view: boolean;
		update: boolean;
		delete: boolean;
		restore: boolean;
	};
}

type MediaPackModalType = 'archive' | 'restore' | null;

function MediaPacksList() {
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const [activeModal, setActiveModal] = useState<MediaPackModalType>(null);
	const [selectedMediaPack, setSelectedMediaPack] = useState<Partial<MediaPack>>();

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index?.title || ':model List',
					'model',
					crud?.models?.media_pack || 'Marketing Image Pack'
				)
			),
		[crud]
	);

	const handleOptionSelect = (action: unknown, data: MediaPack) => {
		const { id = '' } = data;

		const actions: { [key: string]: () => void } = {
			edit: () => {
				navigate(`/media-packs/${id}/edit`);
			},
			archive: () => {
				setSelectedMediaPack(data);
				setActiveModal('archive');
			},
			restore: () => {
				setSelectedMediaPack(data);
				setActiveModal('restore');
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const archiveMediaPack = async () => {
		setDisabled(true);
		try {
			const response = await axios.delete(
				route(API_ENDPOINTS.ADMIN.MEDIA_PACK.DELETE, {
					mediaPack: selectedMediaPack?.id || '',
				})
			);
			customToast({
				title: response?.data?.message || 'Marketing pack deleted successfully.',
				variant: Variants.Success,
			});
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'Error deleting marketing pack.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const restoreMediaPack = async () => {
		setDisabled(true);
		try {
			const response = await axios.patch(
				route(API_ENDPOINTS.ADMIN.MEDIA_PACK.RESTORE, {
					mediaPack: selectedMediaPack?.id || '',
				})
			);
			customToast({
				title: response?.data?.message || 'Marketing pack restored successfully.',
				variant: Variants.Success,
			});
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'Error restoring marketing pack.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const closeModal = () => {
		setActiveModal(null);
		setSelectedMediaPack({});
	};

	const items = useMemo(() => {
		if (!hasPermissionAccess(Permissions.ADMIN_MEDIA_PACK_STORE)) return [];

		return [
			{
				label: StringHelpers.title(crud?.buttons?.create?.default || 'Create'),
				disabled: false,
				onClick: () => navigate('/media-packs/create'),
				icon: <Icon icon={Icons.ADD} />,
			},
		];
	}, [crud, hasPermissionAccess, navigate]);

	const colMapping = (data: MediaPack) => {
		const isDeleted = !!data.deleted_at;

		return [
			{
				id: 'title',
				label: StringHelpers.title(fields?.title || 'Title'),
				value: data.title || '-',
				sortable: true,
			},
			{
				id: 'thumbnail_url',
				label: StringHelpers.title(fields?.thumbnail || 'Thumbnail'),
				value: data.thumbnail_url ? (
					<img
						src={data.thumbnail_url}
						alt={data.title}
						style={{ height: '50px', width: 'auto', objectFit: 'contain' }}
					/>
				) : (
					'-'
				),
			},
			{
				id: 'download_url',
				label: StringHelpers.title(fields?.download_url || 'Download'),
				value: data.download_url ? (
					<a
						href={data.download_url}
						target="_blank"
						rel="noopener noreferrer"
					>
						{crud?.select_labels?.download || 'Download'}
					</a>
				) : (
					'-'
				),
			},
			{
				id: 'created_at',
				label: StringHelpers.title(fields?.created_at || 'Created'),
				value: formatDate(data.created_at) || '-',
				sortable: true,
			},
			{
				id: 'updated_at',
				label: StringHelpers.title(fields?.updated_at || 'Updated'),
				value: formatDate(data.updated_at) || '-',
				sortable: true,
			},
			{
				id: 'deleted_at',
				label: StringHelpers.title(fields?.archived_at || 'Archived'),
				value: formatDate(data.deleted_at) || '-',
				sortable: true,
				visibleCondition: () => isDeleted,
			},
			{
				id: 'actions',
				label: StringHelpers.title(fields?.actions || 'Actions'),
				value: (
					<FormInput
						type="select"
						label={StringHelpers.title(fields?.actions || 'Action')}
						placeholder={StringHelpers.title(fields?.select || 'Select')}
						name="actions"
						value=""
						onChange={(value) => {
							if (!!value && typeof value === 'string') {
								handleOptionSelect(value, data);
							}
						}}
						options={[
							...(data.permissions.update ? 
								[
									{
										label: StringHelpers.title(crud?.buttons?.edit?.default || 'Edit'),
										value: 'edit',
									},
								]
								: []
							),
							...(data.permissions.delete ? 
								[
									{
										label: StringHelpers.title(crud?.buttons?.archive?.default || 'Archive'),
										value: 'archive',
									},
								]
								: []
							),
							...(data.permissions.restore ? 
								[
									{
										label: StringHelpers.title(crud?.buttons?.restore?.default || 'Restore'),
										value: 'restore',
									},
								]
								: []
							),
						]}
						noMargin
					/>
				),
			},
		];
	};

	return (
		<PageLayout title={title || 'Marketing Image Pack List'}>
			<DataTable<MediaPack>
				routeEndpoint={route(API_ENDPOINTS.ADMIN.MEDIA_PACK.INDEX)}
				colMapping={colMapping}
				items={items}
				incrementPaginationButtons
				canViewArchived
				variant={Variants.Info}
				refreshOnChange={selectedMediaPack?.id}
			/>

			<Modal
				open={activeModal === 'archive'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive?.title || 'Archive :name',
						'name',
						crud?.models?.media_pack || 'Marketing Image Pack'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				variant={Variants.Primary}
				onConfirm={archiveMediaPack}
			>
				{crud?.modals?.archive?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									`${selectedMediaPack?.title} ${
										StringHelpers.lower(
											crud?.models?.media_pack 
											|| 'marketing image pack'
										)
									}`
								)}
							</p>
						)
					}
				)}
			</Modal>

			<Modal
				open={activeModal === 'restore'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.restore?.title || 'Restore :name',
						'name',
						crud?.models?.media_pack || 'Marketing Image Pack'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.restore?.submitting || 'Restoring...'
						: crud?.buttons?.restore?.default || 'Restore'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				variant={Variants.Primary}
				onConfirm={restoreMediaPack}
			>
				{crud?.modals?.restore?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									`${selectedMediaPack?.title} ${
										StringHelpers.lower(
											crud?.models?.media_pack 
											|| 'marketing image pack'
										)
									}`
								)}
							</p>
						);
					}
				)}
			</Modal>
		</PageLayout>
	);
}

export default MediaPacksList;
